import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  LocalShipping as TransferIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import ConfirmDialog from "../../components/layout/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useRBAC } from "../../hooks/useRBAC";
import { salesPointAPI } from "../../services/salesPointAPI";
import inventoryAPI from "../../services/inventoryAPI";
import stockTransferAPI, {
  CreateStockTransferPayload,
  StockTransfer,
} from "../../services/stockTransferAPI";
import warehouseAPI from "../../services/warehouseAPI";
import { getApiErrorMessage } from "../../utils/apiError";

type LineDraft = {
  productId: string;
  productLabel: string;
  quantity: string;
  unit?: string;
  packSize?: number;
  conversionFactor?: number;
};

const statusColor = (s: string) => {
  switch (String(s).toUpperCase()) {
    case "RECEIVED":
      return "success";
    case "DISPATCHED":
      return "info";
    case "PENDING":
      return "warning";
    case "CANCELLED":
      return "default";
    default:
      return "secondary";
  }
};

const StockTransfersPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();
  const {
    canRead,
    hasExtraAction,
    canCreate: canCreateModule,
    canUpdate: canUpdateModule,
  } = useRBAC();
  const canCreate =
    canCreateModule("stock_transfers") ||
    canCreateModule("inventory") ||
    canCreateModule("warehouses") ||
    hasExtraAction("stock_transfers", "stock_transfer") ||
    hasExtraAction("inventory", "stock_transfer");
  const canDispatch =
    canUpdateModule("stock_transfers") ||
    canUpdateModule("inventory") ||
    canUpdateModule("warehouses") ||
    hasExtraAction("stock_transfers", "stock_transfer_dispatch") ||
    hasExtraAction("inventory", "stock_transfer_dispatch");
  const canReceive =
    canUpdateModule("stock_transfers") ||
    canUpdateModule("inventory") ||
    canUpdateModule("warehouses") ||
    hasExtraAction("stock_transfers", "stock_transfer_receive") ||
    hasExtraAction("inventory", "stock_transfer_receive");

  const [statusFilter, setStatusFilter] = useState("all");
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(
    null
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<StockTransfer | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [fromSp, setFromSp] = useState("");
  const [toSp, setToSp] = useState("");
  const [fromWh, setFromWh] = useState("");
  const [toWh, setToWh] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { productId: "", productLabel: "", quantity: "1" },
  ]);

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["sales_points", "all"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const { data: warehousesData } = useQuery({
    queryKey: ["warehouses", "transfer-picker"],
    queryFn: () => warehouseAPI.getWarehouses({ pageSize: 200, isActive: true }),
  });
  const warehouses = useMemo(() => warehousesData?.data || [], [warehousesData?.data]);

  // Deep URL param support: e.g. /stock-transfers?fromWh=UUID&create=1
  useEffect(() => {
    const fromWhParam = searchParams.get("fromWh");
    const createParam = searchParams.get("create") || searchParams.get("new");
    if (fromWhParam && warehouses.length > 0) {
      const targetWh = warehouses.find((w) => w.id === fromWhParam);
      if (targetWh) {
        setFromWh(targetWh.id);
        if (targetWh.branchId && salesPoints.length > 0) {
          const matchSp = salesPoints.find(
            (sp: any) => sp.branchId === targetWh.branchId
          );
          if (matchSp) setFromSp(matchSp.id);
        }
      }
    }
    if (createParam === "1" || createParam === "true") {
      setCreateOpen(true);
    }
  }, [searchParams, warehouses, salesPoints]);

  const { data: inventoryQuery } = useQuery({
    queryKey: ["inventory", "for-transfer", fromSp, fromWh],
    queryFn: () =>
      inventoryAPI.getInventory({
        pageSize: 500,
        salesPointId: fromSp,
        warehouseId: fromWh,
      }),
    enabled: Boolean(fromSp) && Boolean(fromWh) && createOpen,
  });

  const inventoryList = useMemo(() => inventoryQuery?.data ?? [], [inventoryQuery]);

  const availableByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of inventoryList) {
      const pid = row.productId || row.product?.id;
      if (!pid) continue;
      map.set(String(pid), Number(row.availableQuantity) || 0);
    }
    return map;
  }, [inventoryList]);

  const productOptions = useMemo(() => {
    return inventoryList
      .filter((row) => {
        const pid = row.productId || row.product?.id;
        return Boolean(pid) && Number(row.availableQuantity) > 0;
      })
      .map((row) => {
        const pid = String(row.productId || row.product?.id);
        const avail = Number(row.availableQuantity) || 0;
        const uom =
          row.unit || row.product?.unit || row.product?.baseUnit || (row.product as any)?.stockUnit || "PCS";
        const packSize = Number((row.product as any)?.packSize) || 1;
        const conversionFactor = Number((row.product as any)?.conversionFactor) || 1;
        const allowFractional = Boolean(row.product?.allowFractionalQty);
        return {
          id: pid,
          label: `${row.product?.name || "Product"} (${row.product?.sku || "—"}) · Avail: ${avail} ${uom}`,
          available: avail,
          unit: uom,
          packSize,
          conversionFactor,
          allowFractional,
        };
      });
  }, [inventoryList]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["stock-transfers", statusFilter],
    queryFn: () =>
      stockTransferAPI.list({
        pageSize: 50,
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      }),
    enabled: canRead("stock_transfers") || canRead("inventory"),
  });

  const transfers = data?.transfers || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["stock-transfers"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateStockTransferPayload) =>
      stockTransferAPI.create(payload),
    onSuccess: (row) => {
      invalidate();
      setCreateOpen(false);
      setFormError(null);
      setDetail(row);
      setAlert({
        type: "success",
        message:
          row.status === "PENDING"
            ? `Transfer ${row.transferNumber} submitted — dispatch when ready`
            : `Draft ${row.transferNumber} saved`,
      });
      resetForm();
    },
    onError: (err) => {
      const msg = getApiErrorMessage(err);
      setFormError(msg);
      setAlert({ type: "error", message: msg });
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: "submit" | "dispatch" | "receive" | "cancel";
      reason?: string;
    }) => {
      if (action === "submit") return stockTransferAPI.submit(id);
      if (action === "dispatch") return stockTransferAPI.dispatch(id);
      if (action === "receive") return stockTransferAPI.receive(id);
      return stockTransferAPI.cancel(id, reason || "Cancelled");
    },
    onSuccess: (row, vars) => {
      invalidate();
      setDetail(row);
      setDetailError(null);
      setCancelOpen(false);
      setCancelReason("");
      const msg =
        vars.action === "dispatch"
          ? `Dispatched ${row.transferNumber} — stock left source warehouse`
          : vars.action === "receive"
            ? `Received ${row.transferNumber} — stock added at destination`
            : vars.action === "cancel"
              ? `Cancelled ${row.transferNumber}`
              : `Transfer ${row.status}`;
      setAlert({ type: "success", message: msg });
    },
    onError: (err) => {
      const msg = getApiErrorMessage(err);
      setDetailError(msg);
      setAlert({ type: "error", message: msg });
    },
  });

  async function openDetail(row: StockTransfer) {
    setDetail(row);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const fresh = await stockTransferAPI.getById(row.id);
      setDetail(fresh);
    } catch (err) {
      setDetailError(getApiErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  }

  function resetForm() {
    setFromSp("");
    setToSp("");
    setFromWh("");
    setToWh("");
    setNotes("");
    setLines([{ productId: "", productLabel: "", quantity: "1" }]);
  }

  function spName(id: string, embedded?: { name?: string } | null) {
    if (embedded?.name) return embedded.name;
    const sp = (salesPoints as any[]).find((s) => s.id === id);
    return sp?.name || id.slice(0, 8);
  }

  function whLabel(
    id?: string | null,
    embedded?: { name?: string; code?: string } | null
  ) {
    if (embedded?.name) {
      return embedded.code ? `${embedded.name} (${embedded.code})` : embedded.name;
    }
    if (!id) return "—";
    const wh = warehouses.find((w) => w.id === id);
    return wh ? `${wh.name} (${wh.code})` : id.slice(0, 8);
  }

  function warehousesForSp(spId: string) {
    const sp = (salesPoints as any[]).find((s) => s.id === spId);
    return warehouses.filter((w) => {
      if (!sp?.branchId) return true;
      return !w.branchId || w.branchId === sp.branchId;
    });
  }

  function buildPayload(submit: boolean): CreateStockTransferPayload | null {
    setFormError(null);
    if (!fromSp || !toSp) {
      const msg = "Select source and destination sales points";
      setFormError(msg);
      setAlert({ type: "error", message: msg });
      return null;
    }
    if (!fromWh || !toWh) {
      const msg = "Select source and destination warehouses (required)";
      setFormError(msg);
      setAlert({ type: "error", message: msg });
      return null;
    }
    if (fromWh === toWh) {
      const msg = "Source and destination warehouses must be different";
      setFormError(msg);
      setAlert({ type: "error", message: msg });
      return null;
    }
    const items = lines
      .filter((l) => l.productId && Number(l.quantity) > 0)
      .map((l) => ({
        productId: l.productId,
        quantity: Number(l.quantity),
        unit: l.unit,
        packSize: l.packSize,
        conversionFactor: l.conversionFactor
      }));
    if (!items.length) {
      const msg = "Add at least one product with quantity";
      setFormError(msg);
      setAlert({ type: "error", message: msg });
      return null;
    }
    for (const item of items) {
      const avail = availableByProduct.get(item.productId) ?? 0;
      const baseQty = (item.packSize && item.packSize > 1)
        ? item.quantity * item.packSize
        : item.conversionFactor && item.conversionFactor !== 1
          ? item.quantity * item.conversionFactor
          : item.quantity;
      if (baseQty > avail + 0.0001) {
        const label =
          productOptions.find((p) => p.id === item.productId)?.label || item.productId;
        const msg = `Base quantity (${baseQty}) exceeds available stock (${avail}) for ${label}`;
        setFormError(msg);
        setAlert({ type: "error", message: msg });
        return null;
      }
    }
    return {
      fromSalesPointId: fromSp,
      toSalesPointId: toSp,
      fromWarehouseId: fromWh,
      toWarehouseId: toWh,
      notes: notes.trim() || undefined,
      submit,
      items,
    };
  }

  async function confirmAction(
    action: "dispatch" | "receive",
    transfer: StockTransfer
  ) {
    const ok = await confirm({
      title: action === "dispatch" ? "Dispatch transfer" : "Receive transfer",
      message:
        action === "dispatch"
          ? `Dispatch ${transfer.transferNumber}? Stock will leave the source warehouse immediately.`
          : `Receive ${transfer.transferNumber}? Stock will be added to the destination warehouse.`,
      confirmLabel: action === "dispatch" ? "Dispatch" : "Receive",
      tone: "warning",
    });
    if (!ok) return;
    actionMutation.mutate({ id: transfer.id, action });
  }

  if (!canRead("stock_transfers") && !canRead("inventory")) {
    return (
      <Alert severity="warning">You do not have permission to view stock transfers.</Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Stock transfers"
        subtitle="Move stock between warehouses. Draft → Submit → Dispatch → Receive."
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Warehouse" },
          { label: "Transfers" },
        ]}
        action={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setFormError(null);
                setCreateOpen(true);
              }}
            >
              New transfer
            </Button>
          ) : undefined
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load transfers
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {["DRAFT", "PENDING", "DISPATCHED", "RECEIVED", "CANCELLED"].map(
              (s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      </Stack>

      <DataListCard>
        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              ) : transfers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No transfers yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => openDetail(t)}
                        startIcon={<TransferIcon fontSize="small" />}
                      >
                        {t.transferNumber}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {spName(t.fromSalesPointId, t.fromSalesPoint)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {whLabel(t.fromWarehouseId, t.fromWarehouse)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {spName(t.toSalesPointId, t.toSalesPoint)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {whLabel(t.toWarehouseId, t.toWarehouse)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.status}
                        color={statusColor(t.status) as any}
                      />
                    </TableCell>
                    <TableCell>{t.items?.length ?? "—"}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openDetail(t)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DataListCard>

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              New stock transfer
            </Typography>
            <IconButton
              size="small"
              onClick={() => !createMutation.isPending && setCreateOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          {formError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Pick warehouses first — product list shows only stock available at the source
            warehouse.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>From sales point</InputLabel>
                <Select
                  label="From sales point"
                  value={fromSp}
                  onChange={(e) => {
                    setFromSp(e.target.value);
                    setFromWh("");
                    setLines([{ productId: "", productLabel: "", quantity: "1" }]);
                  }}
                >
                  {(salesPoints as any[]).map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>
                      {sp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>To sales point</InputLabel>
                <Select
                  label="To sales point"
                  value={toSp}
                  onChange={(e) => {
                    setToSp(e.target.value);
                    setToWh("");
                  }}
                >
                  {(salesPoints as any[]).map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>
                      {sp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>From warehouse</InputLabel>
                <Select
                  label="From warehouse"
                  value={fromWh}
                  onChange={(e) => {
                    setFromWh(e.target.value);
                    setLines([{ productId: "", productLabel: "", quantity: "1" }]);
                  }}
                  disabled={!fromSp}
                >
                  {warehousesForSp(fromSp).map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name} ({w.code}){w.city ? ` · ${w.city}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>To warehouse</InputLabel>
                <Select
                  label="To warehouse"
                  value={toWh}
                  onChange={(e) => setToWh(e.target.value)}
                  disabled={!toSp}
                >
                  {warehousesForSp(toSp)
                    .filter((w) => w.id !== fromWh)
                    .map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name} ({w.code}){w.city ? ` · ${w.city}` : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            {!fromWh ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Select a source warehouse to load products with available stock.
                </Typography>
              </Grid>
            ) : productOptions.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="warning">
                  No available stock at this warehouse. Add inventory first, then transfer.
                </Alert>
              </Grid>
            ) : null}
            {lines.map((line, idx) => {
              const avail = line.productId
                ? availableByProduct.get(line.productId) ?? 0
                : 0;
              const opt = productOptions.find((p) => p.id === line.productId);
              const uom = opt?.unit || "Unit";
              return (
                <Grid item xs={12} key={idx}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                    <FormControl fullWidth>
                      <InputLabel>Product</InputLabel>
                      <Select
                        label="Product"
                        value={line.productId}
                        disabled={!fromWh}
                        onChange={(e) => {
                          const id = e.target.value;
                          const selectedOpt = productOptions.find((p) => p.id === id);
                          setLines((prev) =>
                            prev.map((l, i) =>
                              i === idx
                                ? {
                                    ...l,
                                    productId: id,
                                    productLabel: selectedOpt?.label || "",
                                    unit: selectedOpt?.unit || "PCS",
                                    packSize: selectedOpt?.packSize || 1,
                                    conversionFactor: selectedOpt?.conversionFactor || 1,
                                    quantity: l.quantity || "1",
                                  }
                                : l
                            )
                          );
                        }}
                      >
                        {productOptions.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label={`Qty (${uom})`}
                      type="number"
                      sx={{ width: { sm: 180 } }}
                      value={line.quantity}
                      inputProps={{
                        min: opt?.allowFractional ? 0.001 : 1,
                        step: opt?.allowFractional ? "any" : "1",
                      }}
                      helperText={
                        opt?.packSize && opt.packSize > 1
                          ? `1 ${uom} = ${opt.packSize} Base | Total: ${(Number(line.quantity) || 0) * opt.packSize}`
                          : line.productId ? `Avail: ${avail} ${uom}` : " "
                      }
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l, i) =>
                            i === idx ? { ...l, quantity: e.target.value } : l
                          )
                        )
                      }
                    />
                    <IconButton
                      disabled={lines.length <= 1}
                      onClick={() =>
                        setLines((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <Button
                size="small"
                disabled={!fromWh || productOptions.length === 0}
                onClick={() =>
                  setLines((prev) => [
                    ...prev,
                    { productId: "", productLabel: "", quantity: "1" },
                  ])
                }
              >
                Add line
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            disabled={createMutation.isPending}
            onClick={() => {
              const payload = buildPayload(false);
              if (payload) createMutation.mutate(payload);
            }}
          >
            {createMutation.isPending ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="contained"
            disabled={createMutation.isPending}
            onClick={() => {
              const payload = buildPayload(true);
              if (payload) createMutation.mutate(payload);
            }}
          >
            {createMutation.isPending ? "Submitting…" : "Create & submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" component="span" fontWeight={700}>
                {detail?.transferNumber}
              </Typography>
              <Chip
                size="small"
                label={detail?.status}
                color={statusColor(detail?.status || "") as any}
                sx={{ ml: 1 }}
              />
            </Box>
            <IconButton size="small" onClick={() => setDetail(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          {detailError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDetailError(null)}>
              {detailError}
            </Alert>
          )}
          {detailLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Refreshing…
            </Typography>
          )}
          {detail && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                From: <strong>{spName(detail.fromSalesPointId, detail.fromSalesPoint)}</strong>
                <br />
                Warehouse: {whLabel(detail.fromWarehouseId, detail.fromWarehouse)}
              </Typography>
              <Typography variant="body2">
                To: <strong>{spName(detail.toSalesPointId, detail.toSalesPoint)}</strong>
                <br />
                Warehouse: {whLabel(detail.toWarehouseId, detail.toWarehouse)}
              </Typography>
              {detail.notes ? (
                <Typography variant="body2" color="text.secondary">
                  Notes: {detail.notes}
                </Typography>
              ) : null}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">Unit</TableCell>
                    <TableCell align="center">Pack Size</TableCell>
                    <TableCell align="right">Requested</TableCell>
                    <TableCell align="right">Base Qty</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Received</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.items || []).map((it) => {
                    const unit =
                      it.unit ||
                      it.product?.unit ||
                      it.product?.baseUnit ||
                      it.product?.stockUnit ||
                      "—";
                    const packSize = it.packSize || it.product?.packSize || 1;
                    const baseQty =
                      it.baseQuantity != null ? it.baseQuantity : it.quantityRequested;
                    return (
                      <TableRow key={it.id}>
                        <TableCell>
                          <Typography fontWeight={600} variant="body2">
                            {it.product?.name || it.productId.slice(0, 8)}
                          </Typography>
                          {it.product?.sku ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              SKU: {it.product.sku}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={unit}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {packSize > 1 ? (
                            <Chip
                              size="small"
                              label={`1 × ${packSize}`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {it.quantityRequested}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            {baseQty}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {it.quantitySent}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={
                              Number(it.quantityReceived) > 0 ? "success.main" : "text.secondary"
                            }
                            fontWeight={Number(it.quantityReceived) > 0 ? 600 : 400}
                          >
                            {it.quantityReceived}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail?.status === "DRAFT" && canCreate && (
            <Button
              disabled={actionMutation.isPending}
              onClick={() =>
                actionMutation.mutate({ id: detail.id, action: "submit" })
              }
            >
              Submit
            </Button>
          )}
          {(detail?.status === "DRAFT" || detail?.status === "PENDING") &&
            canDispatch && (
              <Button
                variant="contained"
                disabled={actionMutation.isPending}
                onClick={() => confirmAction("dispatch", detail!)}
              >
                Dispatch
              </Button>
            )}
          {detail?.status === "DISPATCHED" && canReceive && (
            <Button
              variant="contained"
              color="success"
              disabled={actionMutation.isPending}
              onClick={() => confirmAction("receive", detail)}
            >
              Receive
            </Button>
          )}
          {detail &&
            detail.status !== "RECEIVED" &&
            detail.status !== "CANCELLED" &&
            canCreate && (
              <Button
                color="error"
                disabled={actionMutation.isPending}
                onClick={() => setCancelOpen(true)}
              >
                Cancel
              </Button>
            )}
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel transfer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {detail?.status === "DISPATCHED"
              ? "Stock already left the source — cancel will reverse it back to the source warehouse."
              : "This will mark the transfer as cancelled."}
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Optional"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Back</Button>
          <Button
            color="error"
            variant="contained"
            disabled={actionMutation.isPending || !detail}
            onClick={() =>
              detail &&
              actionMutation.mutate({
                id: detail.id,
                action: "cancel",
                reason: cancelReason.trim() || "Cancelled from admin",
              })
            }
          >
            Confirm cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default StockTransfersPage;
